import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { DeleteWarning, DomHelpers, Help, Loading, Modal, Paging, SystemText } from '@salesperformanceint/e4shared';
import BlobImage from 'components/blobImage';
import NoResults from 'components/general/noResults';
import { isAfter, isPast, isSameDay, e4Format } from 'helpers/dates';
import TaskIcon from 'views/user/task/components/taskIcon';
import { taskTitle } from 'helpers/course';
import TableSorter from 'components/general/tableSorter';
import Edit from './components/edit';

export const Enrollments = ({
    data,
    pathways,
    page,
    loading,
    setPage,
    sortBy,
    setSortBy,
    descending,
    setDescending,
    selected,
    setSelected,
    selectAll,
    setSelectAll,
    unassign,
    updateDueDate,
}) => {
    const formatDate = d => (d ? e4Format(d) : '-');
    const [showEdit, setShowEdit] = React.useState(false);
    const [showUnassign, setShowUnassign] = React.useState(false);
    const [showUnassignWarning, setShowUnassignWarning] = React.useState(false);

    const { user } = useSelector(state => state.user);

    const sortProps = {
        sortBy,
        descending,
        setSortBy,
        setDescending,
    };

    const handleButtonClick = React.useCallback(
        e => {
            const btn = DomHelpers.getEventButton(e);

            switch (btn.value) {
                case 'unassign':
                    const assignments = data.assignments.filter(d => selected.some(s => s === d.id));
                    const allNotInprogress = assignments.every(a => !a.inProgress);
                    const oneInProgress = assignments.length === 1 && assignments.every(a => a.inProgress);
                    if (!allNotInprogress && !oneInProgress) {
                        setShowUnassignWarning(true);
                        break;
                    }

                    setShowUnassign(true);
                    break;
                case 'edit':
                    setShowEdit(true);
                    break;
                default:
                    break;
            }
        },
        [selected, data?.assignments]
    );

    const pageChanged = newPage => {
        if (newPage === page) {
            return;
        }

        setSelectAll(false);
        setPage(newPage);
    };

    return (
        <div key="courses">
            <div className="flex flex-center no-wrap">
                <button
                    className="btn btn-sm btn-danger"
                    disabled={!selected?.length}
                    data-testid="button-manage-enrolments"
                    value="unassign"
                    onClick={handleButtonClick}
                >
                    {SystemText('PathwayTracker/Tabs/Enrolments/Buttons/Unenroll')}
                </button>
                <div className="table-btns flex flex-top">
                    <button
                        className="btn btn-sm btn-primary"
                        disabled={!selected?.length}
                        data-testid="button-edit-enrolments"
                        value="edit"
                        onClick={handleButtonClick}
                    >
                        {SystemText('PathwayTracker/Tabs/Enrolments/Buttons/Edit')}
                    </button>
                    <Help id="help-unenroll">{SystemText('PathwayTracker/Tabs/Enrolments/Buttons/Unenroll/Help')}</Help>
                </div>
            </div>
            {data?.recordcount > 0 ? (
                <div>
                    {loading ? (
                        <Loading />
                    ) : (
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th width="1">
                                        <input
                                            type="checkbox"
                                            checked={selectAll}
                                            data-testid="checkbox-select-all"
                                            onChange={() => setSelectAll(!selectAll)}
                                        />
                                    </th>
                                    <th>
                                        <TableSorter default column="pathway" {...sortProps}>
                                            {SystemText('PathwayTracker/Tabs/Enrolments/Table/Headers/Pathway')}
                                        </TableSorter>
                                    </th>
                                    <th>
                                        <TableSorter column="user" {...sortProps}>
                                            {SystemText('PathwayTracker/Tabs/Enrolments/Table/Headers/TeamMember')}
                                        </TableSorter>
                                    </th>
                                    <th>
                                        <TableSorter column="enrolled" {...sortProps}>
                                            {SystemText('PathwayTracker/Tabs/Enrolments/Table/Headers/Enrolled')}
                                        </TableSorter>
                                    </th>
                                    <th>
                                        <TableSorter column="launch" {...sortProps}>
                                            {SystemText('PathwayTracker/Tabs/Enrolments/Table/Headers/Launch')}
                                        </TableSorter>
                                    </th>
                                    <th>
                                        <TableSorter column="due" {...sortProps}>
                                            {SystemText('PathwayTracker/Tabs/Enrolments/Table/Headers/Due')}
                                        </TableSorter>
                                    </th>
                                    <th>
                                        <TableSorter column="completed" {...sortProps}>
                                            {SystemText('PathwayTracker/Tabs/Enrolments/Table/Headers/Completed')}
                                        </TableSorter>
                                    </th>
                                    <th>{SystemText('PathwayTracker/Tabs/Enrolments/Table/Headers/Progress')}</th>
                                    {user.administrator && <th></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {data?.assignments?.map((row, i) => {
                                    const selectRow = () => {
                                        setSelected(prev => {
                                            if (prev.some(x => x === row.id)) {
                                                return prev.filter(x => x !== row.id);
                                            } else {
                                                return prev.concat([row.id]);
                                            }
                                        });
                                    };

                                    const pathway = pathways.find(p => p.id === row.pathway_Id);
                                    const json = pathway?.competency_Values;
                                    const competencyValues = json ? JSON.parse(json) : [];
                                    const link = user?.companySettings?.prism_UI
                                        ? `/team-member/${row.profile_Id}?pathwayId=${row.id}`
                                        : `/develop/pathways/${row.profile_Id}`;

                                    return (
                                        <tr
                                            key={`progress-${row.id}`}
                                            data-assignment-id={row.id}
                                            data-pathway-id={row.pathway_Id}
                                        >
                                            <td width="1">
                                                <input
                                                    type="checkbox"
                                                    data-testid="checkbox-select-row"
                                                    checked={selected.some(x => x === row.id)}
                                                    onChange={selectRow}
                                                    disabled={row.date_Completed}
                                                />
                                            </td>
                                            <td>{row.pathway}</td>
                                            <td>
                                                <Link to={link}>
                                                    <BlobImage
                                                        height="30"
                                                        className="img-circle mr10"
                                                        folder="avatar"
                                                        initials={row.initials}
                                                        key={row.avatar}
                                                        src={row.avatar}
                                                        alt={row.full_Name}
                                                    />
                                                    {row.full_Name}
                                                </Link>
                                            </td>
                                            <td>{formatDate(row.date_Assigned)}</td>
                                            <td>
                                                <span>{formatDate(row.launch, true)}</span>
                                            </td>
                                            <td>
                                                <span>{formatDate(row.date_Due)}</span>
                                                {!row.date_Completed && isPast(row.date_Due) && (
                                                    <i
                                                        title={SystemText('PathwayTracker/Tabs/Enrolments/Overdue')}
                                                        className="ml10 fa fa-warning text-danger"
                                                    />
                                                )}
                                            </td>
                                            <td>
                                                {formatDate(row.date_Completed)}
                                                {row.date_Completed &&
                                                    isAfter(row.date_Completed, row.date_Due) &&
                                                    !isSameDay(row.date_Completed, row.date_Due) && (
                                                        <i
                                                            title={SystemText('PathwayTracker/Tabs/Enrolments/Late')}
                                                            className="ml10 fa fa-warning text-danger"
                                                        />
                                                    )}
                                            </td>
                                            <td>
                                                <div className="flex flex-center flex-row pathway-progress text-lg mlm10">
                                                    {(row.competencies || []).map((c, j) => {
                                                        const required = competencyValues[c.id];
                                                        const actual = c.rating;
                                                        return (
                                                            <div
                                                                className="flex flex-center flex-row"
                                                                key={`competency-${c.id}-{i}-${j}`}
                                                            >
                                                                <div>
                                                                    <Help
                                                                        id={`competency-${c.id}-${i}-${j}-help`}
                                                                        target={
                                                                            <i
                                                                                className={`fa fa-star ${
                                                                                    required > actual
                                                                                        ? 'text-danger'
                                                                                        : 'text-success'
                                                                                } ml10`}
                                                                            />
                                                                        }
                                                                    >
                                                                        <div>
                                                                            <div>{c.name}</div>
                                                                            <div>
                                                                                {SystemText(
                                                                                    'PathwayTracker/Tabs/Enrolments/Required'
                                                                                )}
                                                                                : {required}
                                                                            </div>
                                                                            <div>
                                                                                {SystemText(
                                                                                    'PathwayTracker/Tabs/Enrolments/Current'
                                                                                )}
                                                                                : {actual}
                                                                            </div>
                                                                        </div>
                                                                    </Help>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                    {(row.tasks || []).map(t => {
                                                        const colour = !t.date_Completed
                                                            ? 'text-danger'
                                                            : t.requires_Manager && !t.approved
                                                              ? 'text-warning'
                                                              : t.metric_Task && !t.goal_Met
                                                                ? 'text-danger'
                                                                : 'text-success';
                                                        return (
                                                            <div key={`tasks-${t.id}`}>
                                                                <Help
                                                                    id={`tasks-${row.profile_Id}-${t.id}-help`}
                                                                    target={
                                                                        <TaskIcon
                                                                            extraClass={colour}
                                                                            icons={
                                                                                t.icons?.length
                                                                                    ? t.icons
                                                                                    : ['calendar-check']
                                                                            }
                                                                        />
                                                                    }
                                                                >
                                                                    <div>{taskTitle(t)}</div>
                                                                </Help>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                    <div className="ml10 mtm5 mb10">
                        <i className={`fa ${selected.length ? 'fa-check text-success' : 'fa-times text-danger'} mr5`} />
                        <span>{SystemText('PathwayTracker/Tabs/Enrolments/Selected', selected.length)}</span>
                    </div>
                    <div className="text-center mb10 mtm10">
                        <Paging recordCount={data.recordcount} page={page} goToPage={pageChanged} pageSize={20} />
                    </div>
                </div>
            ) : (
                <NoResults
                    title={SystemText('PathwayTracker/Tabs/Enrolments/NoResults/Title')}
                    message={SystemText('PathwayTracker/Tabs/Enrolments/NoResults/Message')}
                />
            )}
            <DeleteWarning
                message={SystemText('PathwayTracker/Tabs/Enrolments/Unenroll/Warning')}
                show={showUnassign}
                close={confirm => {
                    unassign(confirm, selected, () => setShowUnassign(false));
                }}
            />
            <Edit
                show={showEdit}
                onClose={() => setShowEdit(false)}
                onSave={due => {
                    updateDueDate(due, () => {
                        setShowEdit(false);
                        setSelected([]);
                    });
                }}
                selected={selected}
                assignments={data?.assignments}
            />
            <Modal
                show={showUnassignWarning}
                close={() => setShowUnassignWarning(false)}
                footer={null}
                title={SystemText('General/Controls/Warning/Title/Warning')}
            >
                <div className="flex flex-center">
                    <div className="mr10">
                        <i className="fa fa-warning fa-lg text-danger" />
                    </div>
                    <div>{SystemText('PathwayTracker/Tabs/Enrolments/Buttons/Unenroll/Warning')}</div>
                </div>
            </Modal>
        </div>
    );
};

export default Enrollments;
